import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';

import { PlanMenu, RowAction } from '../task/RowActions.js';
import { shownStatus, useClosingHold } from '../task/closing.js';
import { useLanded } from '../task/landed.js';
import { pointerListeners } from '../task/drag.js';
import { ClosingTimer, DoneToggle, TaskMarks, openOnBlankClick } from '../task/TaskParts.js';
import { useMousePointer } from '../view-mode.js';

import {
  buildColumns,
  columnDroppableId,
  planDrop,
  statusAt,
  statusOfDroppable,
  withPreview,
} from './columns.js';

import type { ClosingHold } from '../task/closing.js';
import type { BoardColumn, Preview } from './columns.js';
import type { Placement } from '../task/landed.js';
import type { TaskExtras, TaskHandlers } from '../task/TaskParts.js';
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import type { Task } from '@nedelnik/shared';

/**
 * Доска: три колонки, перетаскивание между ними и переупорядочивание внутри
 * (3.1.3). Перетаскивание — не единственный способ сменить статус: степперы на
 * каждой карточке обязательны, потому что с телефона таскать неудобно (3.1.4).
 *
 * `@dnd-kit` выбран за поддержку pointer- и touch-событий (раздел 10
 * архитектуры). Датчик указателя срабатывает не сразу, а после нескольких
 * пикселей движения: иначе он съедал бы нажатия на степперы внутри карточки.
 */
export function Board({
  tasks,
  extras,
  handlers,
  onDrop,
}: {
  tasks: readonly Task[];
  extras: TaskExtras;
  handlers: TaskHandlers;
  onDrop: (task: Task, placement: Placement) => void;
}): React.ReactElement {
  // Закрытая задача пару секунд остаётся в своей колонке (3.1.8): переезд в
  // «Готово» без паузы проходит незамеченным.
  const closing = useClosingHold(tasks, { close: handlers.onClose, reopen: handlers.onReopen });
  const [dragging, setDragging] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<Preview | undefined>(undefined);

  const { shown, land } = useLanded(tasks);
  const columns = withPreview(buildColumns(shown, closing.statusOf), preview);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Пауза перед захватом на тач-экране — плата за то, чтобы страница
    // по-прежнему прокручивалась пальцем поверх карточек.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Переезд между колонками — во время перетаскивания, а не по броску (см.
   * `withPreview`). Внутри своей колонки карточки расступаются сами
   * (`SortableContext`), и место там считает бросок.
   */
  function handleDragOver({ active, over }: DragOverEvent): void {
    if (over === null) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (overId === activeId) return;

    const from = statusAt(columns, activeId);
    const to = statusAt(columns, overId);
    if (from === undefined || to === undefined || from === to) return;

    const target = columns.find((column) => column.status === to);
    if (target === undefined) return;

    let index = target.tasks.length;
    if (statusOfDroppable(overId) === undefined) {
      // Над нижней половиной чужой карточки задача встаёт под ней, над
      // верхней — над ней: иначе под последнюю карточку не попасть, не уводя
      // курсор ниже колонки.
      const overIndex = target.tasks.findIndex((task) => task.id === overId);
      const dragged = active.rect.current.translated;
      const below = dragged !== null && dragged.top > over.rect.top + over.rect.height / 2;
      index = overIndex < 0 ? target.tasks.length : overIndex + (below ? 1 : 0);
    }

    setPreview({ id: activeId, status: to, index });
  }

  function handleDragEnd(event: DragEndEvent): void {
    setDragging(undefined);
    setPreview(undefined);
    // Брошена мимо: карточка возвращается туда, откуда её взяли, — и тень
    // летит назад. Это единственный случай, когда назад и надо.
    if (event.over === null) return;

    const activeId = String(event.active.id);
    const placement = planDrop(columns, activeId, String(event.over.id));
    const task = tasks.find((item) => item.id === activeId);
    if (placement === undefined || task === undefined) return;
    if (task.status === placement.status && task.order_key === placement.order_key) return;

    land(task.id, placement);
    onDrop(task, placement);
  }

  function handleDragCancel(): void {
    setDragging(undefined);
    setPreview(undefined);
  }

  const active = dragging === undefined ? undefined : shown.find((task) => task.id === dragging);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(event: DragStartEvent) => setDragging(String(event.active.id))}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <div className="board">
        {columns.map((column) => (
          <Column
            key={column.status}
            column={column}
            extras={extras}
            handlers={handlers}
            closing={closing}
          />
        ))}
      </div>

      {/*
        Тень тащимой карточки рисуется поверх страницы: карточка на своём месте
        остаётся видимой и бледнеет, поэтому видно, откуда и куда идёт задача.

        В руке — вся карточка, со значками и отметками, а не одно название:
        голый заголовок выглядел другим, упрощённым предметом (замечание
        владельца, 2026-09-13). Копия `inert` и скрыта от озвучки: нажимать в
        ней нечего, а второй набор одинаково названных кнопок путал бы и
        озвучку, и тесты.
      */}
      <DragOverlay>
        {active === undefined ? null : (
          <article className={`card card--${active.status} card--overlay`} aria-hidden="true" inert>
            <CardFace task={active} extras={extras} handlers={handlers} closing={closing} />
          </article>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function Column({
  column,
  extras,
  handlers,
  closing,
}: {
  column: BoardColumn;
  extras: TaskExtras;
  handlers: TaskHandlers;
  closing: ClosingHold;
}): React.ReactElement {
  return (
    <section className="column" aria-label={column.title}>
      <h2 className="column__title">
        <i className={`dot dot--${column.status}`} aria-hidden="true" />
        {column.title}
        <span className="count">{column.tasks.length}</span>
      </h2>
      <ColumnStack column={column} extras={extras} handlers={handlers} closing={closing} />
    </section>
  );
}

/**
 * Пустая колонка тоже должна принимать карточку, поэтому целью назначен весь
 * столбец, а не только карточки в нём: иначе задачу некуда положить, пока в
 * колонке нет ни одной задачи.
 */
function ColumnStack({
  column,
  extras,
  handlers,
  closing,
}: {
  column: BoardColumn;
  extras: TaskExtras;
  handlers: TaskHandlers;
  closing: ClosingHold;
}): React.ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: columnDroppableId(column.status) });

  return (
    <SortableContext
      items={column.tasks.map((task) => task.id)}
      strategy={verticalListSortingStrategy}
    >
      <div ref={setNodeRef} className={`column__stack${isOver ? ' is-over' : ''}`}>
        {column.tasks.map((task) => (
          <Card key={task.id} task={task} extras={extras} handlers={handlers} closing={closing} />
        ))}
        {column.tasks.length === 0 && <p className="column__empty">пусто</p>}
      </div>
    </SortableContext>
  );
}

function Card({
  task,
  extras,
  handlers,
  closing,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
  closing: ClosingHold;
}): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  return (
    /*
      Карточку берут целиком (замечание владельца, 2026-09-13): видимая ручка
      посреди строки шумела на каждой карточке. На саму карточку уходят только
      слушатели указателя и касания, а роль и атрибуты `@dnd-kit` — нет:
      карточка с ролью кнопки обернула бы собой значки, а кнопка внутри кнопки
      — сломанная разметка (грабли прототипа).

      Нажатие по значку перетаскиванием не становится: датчик срабатывает
      после сдвига на несколько пикселей (см. `sensors` выше), а клик после
      броска `@dnd-kit` гасит сам — задача не открывается от того, что её
      отпустили.
    */
    <article
      ref={setNodeRef}
      className={`card card--${shownStatus(task, closing)}${isDragging ? ' is-dragging' : ''}${
        closing.isClosing(task) ? ' row--closing' : ''
      }`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...pointerListeners(listeners)}
      onClick={(event) => openOnBlankClick(event, () => handlers.onOpen(task))}
    >
      <CardFace task={task} extras={extras} handlers={handlers} closing={closing}>
        {/*
          Ручка осталась для клавиатуры и озвучки, глазу она не видна (см.
          `.card__grip` в стилях). Клавиатурный слушатель живёт только здесь.
        */}
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="card__grip"
          aria-label={`Перетащить задачу «${task.title ?? ''}»`}
          {...attributes}
          onKeyDown={keyboardListener(listeners)}
        />
      </CardFace>
      {closing.isClosing(task) && <ClosingTimer />}
    </article>
  );
}

/**
 * Лицо карточки: название, действия и отметки.
 *
 * Общее у карточки в колонке и у её копии в руке: отдельная разметка для руки
 * рано или поздно разъехалась бы с доской, и несли бы не ту карточку, что
 * лежит в колонке.
 */
function CardFace({
  task,
  extras,
  handlers,
  closing,
  children,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
  closing: ClosingHold;
  /** Что встаёт между названием и действиями: у карточки в колонке — ручка. */
  children?: React.ReactNode;
}): React.ReactElement {
  const mouse = useMousePointer();

  return (
    <>
      <div className="card__top">
        <button type="button" className="card__title" onClick={() => handlers.onOpen(task)}>
          {task.title}
        </button>
        {children}
        {/*
          С мышью значка действия в строке названия нет вовсе (3.1.10): «в
          работу» и «в бэклог» — соседняя колонка в одном коротком движении, а
          выбор спринта у задачи из Входящих переехал вниз кнопкой словом. Не
          рисуется, а не прячется стилями — спрятанная кнопка остаётся в обходе
          с клавиатуры.
        */}
        {!mouse && <RowAction task={task} extras={extras} handlers={handlers} />}
        <DoneToggle task={task} done={closing.isDone(task)} onToggle={() => closing.toggle(task)} />
      </div>

      <TaskMarks task={task} extras={extras} handlers={handlers} className="card__marks" />

      {/*
        «Запланировать» — под отметками, во всю ширину и при наведении (см.
        `.card__plan` в стилях). Из Входящих перетащить задачу в неделю
        некуда, поэтому выбор недели обязан быть в одном клике; у закрытой
        задачи действий нет (3.1.9).
      */}
      {mouse && task.sprint === null && task.status !== 'done' && (
        <div className="card__plan">
          <PlanMenu task={task} extras={extras} handlers={handlers} wide />
        </div>
      )}
    </>
  );
}

function keyboardListener(
  listeners: DraggableSyntheticListeners,
): React.KeyboardEventHandler | undefined {
  return listeners?.['onKeyDown'] as React.KeyboardEventHandler | undefined;
}
